import { normalizeViteEnvString } from "@/lib/viteEnv";

/** Supabase Auth provider ids. Microsoft / Entra ID is `azure` in Supabase, not `microsoft`. */
export type OAuthProviderId = "google" | "azure";

export interface OAuthProviderOption {
  id: OAuthProviderId;
  /** Button copy. English only — the app serves US school districts. */
  label: string;
}

/** The subset of `import.meta.env` this module reads, injected so tests never mutate env. */
export interface OAuthEnv {
  VITE_OAUTH_GOOGLE_ENABLED?: string;
  VITE_OAUTH_MICROSOFT_ENABLED?: string;
}

const GOOGLE: OAuthProviderOption = { id: "google", label: "Continue with Google" };
const MICROSOFT: OAuthProviderOption = { id: "azure", label: "Continue with Microsoft" };

const DISPLAY_NAMES: Record<string, string> = { google: "Google", azure: "Microsoft" };

/**
 * Which provider buttons the sign-in screen offers, in render order.
 *
 * Both providers are on unless explicitly disabled, now that Google and
 * Microsoft are both configured in Supabase. An unavailable provider is
 * hidden rather than shown disabled — a dead button only generates support
 * tickets.
 */
export function getEnabledOAuthProviders(env: OAuthEnv = import.meta.env as OAuthEnv): OAuthProviderOption[] {
  const providers: OAuthProviderOption[] = [];
  if (normalizeViteEnvString(env.VITE_OAUTH_GOOGLE_ENABLED) !== "false") providers.push(GOOGLE);
  if (normalizeViteEnvString(env.VITE_OAUTH_MICROSOFT_ENABLED) !== "false") providers.push(MICROSOFT);
  return providers;
}

/**
 * Extra OAuth scopes to request per provider, on top of what Supabase Auth
 * asks for by default. `undefined` means "the default is already right".
 *
 * Microsoft needs `email` explicitly. Supabase's azure provider requests only
 * `openid`, so the id_token comes back with no `email` claim and Supabase
 * rejects the callback with "Error getting user email from external provider".
 * Google's provider already defaults to `email profile`, which is why only
 * Microsoft was broken.
 *
 * Supabase splits this value on **commas**, not spaces. A space-separated
 * string is sent to the provider as one malformed scope, so more scopes go in
 * as `"email,profile"`.
 */
export const OAUTH_PROVIDER_SCOPES: Record<OAuthProviderId, string | undefined> = {
  google: undefined,
  azure: "email",
};

/** Human-readable provider name for UI copy ("Confirm your Google account"). */
export function getProviderDisplayName(provider: string): string {
  return DISPLAY_NAMES[provider] ?? provider;
}
